// Provider output validation — pre-persist and pre-UI boundary.
import type { z } from 'zod';
import {
  AssumptionOutputSchema,
  CharacterOptionsOutputSchema,
  ConnectedQuestionsOutputSchema,
  RuntimeSceneOutputSchema,
  SummaryOutputSchema,
  WorldDevicesOutputSchema,
} from './schemas.js';
import {
  DIRECTOR_OUTPUT_SCHEMA_VERSION,
  directorError,
  type DirectorFailureKind,
  type StructuredDirectorError,
} from './errors.js';

export const MAX_PROVIDER_JSON_CHARS = 120_000;

export const OUTPUT_SCHEMA_REGISTRY = {
  ConnectedQuestionsOutput: ConnectedQuestionsOutputSchema,
  AssumptionOutput: AssumptionOutputSchema,
  WorldDevicesOutput: WorldDevicesOutputSchema,
  CharacterOptionsOutput: CharacterOptionsOutputSchema,
  RuntimeSceneOutput: RuntimeSceneOutputSchema,
  SummaryOutput: SummaryOutputSchema,
} as const;

export type OutputSchemaId = keyof typeof OUTPUT_SCHEMA_REGISTRY;

export function parseProviderJson(
  rawText: string,
  schemaId?: string,
): { ok: true; value: unknown } | { ok: false; error: StructuredDirectorError } {
  if (rawText.length > MAX_PROVIDER_JSON_CHARS) {
    return {
      ok: false,
      error: directorError('oversized', 'Provider response exceeded size limit.', {
        schemaId,
        details: [`length=${rawText.length}`, `max=${MAX_PROVIDER_JSON_CHARS}`],
      }),
    };
  }
  try {
    return { ok: true, value: JSON.parse(rawText) as unknown };
  } catch {
    return {
      ok: false,
      error: directorError('invalid_json', 'Provider returned invalid JSON.', { schemaId }),
    };
  }
}

function classifyZodFailure(issues: z.ZodIssue[]): DirectorFailureKind {
  if (issues.some((i) => i.code === 'unrecognized_keys')) return 'unknown_fields';
  if (issues.some((i) => i.code === 'invalid_literal' && i.path.includes('schemaVersion'))) {
    return 'version_mismatch';
  }
  if (issues.some((i) => i.code === 'invalid_type' && i.received === 'undefined')) {
    return 'missing_required';
  }
  return 'schema_mismatch';
}

export function validateDirectorOutput<T>(
  schemaId: OutputSchemaId | string,
  schema: z.ZodType<T>,
  raw: unknown,
): { ok: true; data: T } | { ok: false; error: StructuredDirectorError } {
  const result = schema.safeParse(raw);
  if (result.success) return { ok: true, data: result.data };

  const kind = classifyZodFailure(result.error.issues);
  const details = result.error.issues.map((i) => {
    const path = i.path.length ? i.path.join('.') : '(root)';
    return `${path}: ${i.message}`;
  });

  return {
    ok: false,
    error: directorError(kind, 'Provider output failed schema validation.', {
      schemaId,
      details,
    }),
  };
}

export function validateDirectorOutputById(
  schemaId: OutputSchemaId,
  raw: unknown,
): { ok: true; data: unknown } | { ok: false; error: StructuredDirectorError } {
  const schema = OUTPUT_SCHEMA_REGISTRY[schemaId] as z.ZodType<unknown>;
  return validateDirectorOutput(schemaId, schema, raw);
}

export function parseAndValidateDirectorOutput<T>(
  schemaId: OutputSchemaId,
  schema: z.ZodType<T>,
  rawText: string,
): { ok: true; data: T } | { ok: false; error: StructuredDirectorError } {
  const parsed = parseProviderJson(rawText, schemaId);
  if (!parsed.ok) return parsed;
  return validateDirectorOutput(schemaId, schema, parsed.value);
}

export function assertDirectorSchemaVersion(raw: unknown): StructuredDirectorError | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const version = (raw as { schemaVersion?: unknown }).schemaVersion;
  if (version === undefined) return null;
  if (version !== DIRECTOR_OUTPUT_SCHEMA_VERSION) {
    return directorError('version_mismatch', 'Provider output schema version is not supported.', {
      details: [`expected=${DIRECTOR_OUTPUT_SCHEMA_VERSION}`, `received=${String(version)}`],
    });
  }
  return null;
}
